/**
 * Business timezone SSOT helpers (IANA only).
 * No customer-specific literals. Platform fallbacks are never shown as hydrated customer data.
 */

export type BusinessTimezoneOption = {
  value: string;
  label: string;
};

/** Friendly labels for common zones; values are always IANA identifiers. */
const FRIENDLY_TIMEZONE_LABELS: Readonly<Record<string, string>> = {
  "America/Los_Angeles": "Pacific (Los Angeles)",
  "America/Denver": "Mountain (Denver)",
  "America/Phoenix": "Arizona (Phoenix)",
  "America/Chicago": "Central (Chicago)",
  "America/New_York": "Eastern (New York)",
  "America/Anchorage": "Alaska (Anchorage)",
  "Pacific/Honolulu": "Hawaii (Honolulu)",
  "America/Toronto": "Eastern (Toronto)",
  "America/Vancouver": "Pacific (Vancouver)",
  "Europe/London": "UK (London)",
  "Europe/Paris": "Central Europe (Paris)",
  "Asia/Tokyo": "Japan (Tokyo)",
  "Asia/Singapore": "Singapore",
  "Australia/Sydney": "Australia (Sydney)",
  UTC: "UTC",
};

/** Friendly zones in display order; always offered, even when Intl.supportedValuesOf is unavailable. */
const CURATED_FALLBACK_ZONES: ReadonlyArray<string> = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Phoenix",
  "America/Chicago",
  "America/New_York",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Vancouver",
  "Europe/London",
  "Europe/Paris",
  "Asia/Tokyo",
  "Asia/Singapore",
  "Australia/Sydney",
  "UTC",
];

function labelForIana(iana: string): string {
  return FRIENDLY_TIMEZONE_LABELS[iana] ?? iana;
}

function browserTimezones(): string[] {
  try {
    const intlAny = Intl as unknown as {
      supportedValuesOf?: (key: string) => string[];
    };
    if (typeof intlAny.supportedValuesOf === "function") {
      return intlAny.supportedValuesOf("timeZone");
    }
  } catch {
    // fall through
  }
  return [];
}

export type BusinessTimezoneOptionGroups = {
  /** The curated friendly zones, always present. */
  friendly: BusinessTimezoneOption[];
  /** Remaining browser IANA zones (raw names), alphabetical; empty when the browser list is unavailable. */
  all: BusinessTimezoneOption[];
};

/**
 * Selector options split into the friendly commons and every other browser IANA zone.
 * Persisted values are always IANA identifiers (never PST/PDT).
 */
export function listBusinessTimezoneOptionGroups(): BusinessTimezoneOptionGroups {
  const friendly = CURATED_FALLBACK_ZONES.map((value) => ({
    value,
    label: labelForIana(value),
  }));
  const all = browserTimezones()
    .filter((z) => !CURATED_FALLBACK_ZONES.includes(z))
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: value }));
  return { friendly, all };
}

/** Flat list: friendly commons first, then remaining IANA zones alphabetically. */
export function listBusinessTimezoneOptions(): BusinessTimezoneOption[] {
  const { friendly, all } = listBusinessTimezoneOptionGroups();
  return [...friendly, ...all];
}

/** @deprecated Prefer listBusinessTimezoneOptions() — kept for tests/import stability. */
export const BUSINESS_TIMEZONE_OPTIONS: ReadonlyArray<BusinessTimezoneOption> =
  listBusinessTimezoneOptions();

export function parseBusinessTimezoneFromConfig(
  business: unknown
): string | null {
  if (!business || typeof business !== "object") return null;
  const tz = (business as { timezone?: unknown }).timezone;
  if (typeof tz !== "string") return null;
  const trimmed = tz.trim();
  return trimmed || null;
}

export function canSaveBusinessTimezone(opts: {
  loaded: boolean;
  timezone: string | null;
}): boolean {
  return (
    opts.loaded === true &&
    typeof opts.timezone === "string" &&
    opts.timezone.trim().length > 0
  );
}

export function timezoneSelectValue(
  timezone: string | null,
  options: ReadonlyArray<BusinessTimezoneOption> = listBusinessTimezoneOptions()
): string {
  if (!timezone) return "";
  if (options.some((o) => o.value === timezone)) return timezone;
  return timezone;
}
